import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import { Form } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';
import { ModalFrame, ModalFrameEntity, ModalFrameFooter, ModalFrameMessage } from 'components/modal';

import { releasePartialPayout, releaseTotalPayouts } from 'modules/campaigns/reducers/payouts/campaign-payouts';

import { renderPrice } from 'utils/helper/price';
import { __t, applyTranslations } from 'utils/language';

import './campaign-payouts-release-modal.scss';

import type { SubcampaignInfluencerProfile } from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./campaign-payouts-release-modal.i18n.json'));

type OwnProps = {
  influencerProfile: SubcampaignInfluencerProfile | undefined | null,
  payoutId?: number,
  campaignId: number,
  subcampaignId: number,
  totalBudget: number,
  isNegotiationReopened: boolean,
  onPayoutRelease?: () => void, // eslint-disable-line
  onCloseModal: () => void
};

type DispatchProps = {
  dispatchReleaseTotalPayouts: (campaignId: number, subcampaignId: number) => void,
  dispatchReleasePartialPayout: (payoutId: number, campaignId: number, subcampaignId: number) => void
};

type Props = OwnProps & DispatchProps;

type State = {
  showOverlay: boolean
};

const msgKey = 'campaign_payouts_release_modal';

class CampaignPayoutsReleaseModal extends React.PureComponent<Props, State> {
  state = {
    showOverlay: this.props.isNegotiationReopened
  };

  handleTotalRelease = (): void => {
    const { campaignId, subcampaignId, onCloseModal, dispatchReleaseTotalPayouts } = this.props;

    dispatchReleaseTotalPayouts(campaignId, subcampaignId);
    onCloseModal();
  };

  handlePartialRelease = (): void => {
    const { payoutId, campaignId, subcampaignId, onCloseModal, dispatchReleasePartialPayout } = this.props;

    if (payoutId) {
      dispatchReleasePartialPayout(payoutId, campaignId, subcampaignId);
      onCloseModal();
    }
  };

  handleToggleOverlay = (): void => {
    this.setState(state => ({
      showOverlay: !state.showOverlay
    }));
  };

  renderOverlay(): React.ReactNode {
    const { showOverlay } = this.state;

    const overlayClasses = classnames('campaign-payouts-release-modal__overlay', {
      'campaign-payouts-release-modal__overlay--show': showOverlay
    });

    return (
      <div className={overlayClasses}>
        <div className="campaign-payouts-release-modal__overlay-title">
          {__t(`${msgKey}.labels.watch_out`)}
        </div>
        <div className="campaign-payouts-release-modal__overlay-body">
          {__t(`${msgKey}.messages.watch_out`)}
        </div>
        <div className="campaign-payouts-release-modal__overlay-button">
          <ActionButton
            color="secondary"
            onClick={this.handleToggleOverlay}
          >
            {__t(`${msgKey}.buttons.got_it`)}
          </ActionButton>
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    const { influencerProfile, payoutId, totalBudget, onCloseModal } = this.props;
    const influencerName = influencerProfile && (influencerProfile.artistName || influencerProfile.name) || '';

    return (
      <Form onSubmit={payoutId ? this.handlePartialRelease : this.handleTotalRelease}>
        <div
          className="campaign-payouts-release-modal"
          data-qa="campaign-payouts-release-modal"
        >
          <ModalFrame
            title={`${__t(`${msgKey}.labels.release_payment`)}:`}
          >
            <ModalFrameEntity
              influencerProfile={influencerProfile}
            />
            <ModalFrameMessage
              message={`${__t(`${msgKey}.labels.release_amount`)}:`}
            />
            <ModalFrameMessage
              message={`<em>${renderPrice(totalBudget)}</em>`}
            />
            <ModalFrameMessage
              message={__t(`${msgKey}.messages.are_you_sure`, { influencerName })}
            />
            <ModalFrameFooter>
              <ActionButton
                color="tertiary"
                onClick={onCloseModal}
              >
                {__t('collabary_common.action_button.cancel')}
              </ActionButton>
              <ActionButton type="submit">
                {__t(`${msgKey}.buttons.release`)}
              </ActionButton>
            </ModalFrameFooter>
            {
              this.renderOverlay()
            }
          </ModalFrame>
        </div>
      </Form>
    );
  }
}

const mapDispatchToProps = (dispatch, { onPayoutRelease }: OwnProps): DispatchProps => ({
  dispatchReleaseTotalPayouts: (campaignId, subcampaignId): void => {
    dispatch(releaseTotalPayouts(campaignId, subcampaignId));
  },

  dispatchReleasePartialPayout: (payoutId, campaignId, subcampaignId): void => {
    dispatch(releasePartialPayout(payoutId, campaignId, subcampaignId, onPayoutRelease));
  }
});

export default connect(null, mapDispatchToProps)(CampaignPayoutsReleaseModal);
